// src/components/ProjectStatusPanel.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';

const API_BASE = ''; // relative -> dev proxy (same-origin, no CORS)

// preset task lists (editable per level by the user)
const FOUNDATION_TASKS = [
  'Excavation', 'PCC', 'Footing / column reinforcement',
  'Concreting', 'Backfilling', 'Plinth beam',
];
const FLOOR_TASKS = [
  'Column casting', 'Slab shuttering', 'Slab reinforcement',
  'Slab concreting', 'Blockwork / walls', 'Plastering',
];

const colorFor = (pct) => {
  if (pct <= 0) return '#ef4444';       // red
  if (pct >= 100) return '#22c55e';     // green
  return '#eab308';                     // yellow
};

// short date for the tight left column: "12 Apr"
const fmtShortDate = (ts) =>
  ts ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null;

// build the default level list from the massing floor count
function ordinalFloorName(i) {
  // i = 1 -> Ground Floor, 2 -> 1st Floor, 3 -> 2nd Floor, ...
  if (i === 1) return 'Ground Floor';
  const n = i - 1; // 1st, 2nd, 3rd...
  const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
  return `${n}${suffix} Floor`;
}

function defaultLevels(floors) {
  const levels = [{
    id: 'foundation', name: 'Foundation',
    tasks: FOUNDATION_TASKS.map((t) => ({ label: t, done: false })),
  }];
  for (let i = 1; i <= floors; i++) {
    levels.push({
      id: `floor_${i}`,
      name: ordinalFloorName(i),
      tasks: FLOOR_TASKS.map((t) => ({ label: t, done: false })),
    });
  }
  return levels;
}

const pctOf = (lvl) => {
  if (!lvl.tasks.length) return 0;
  const done = lvl.tasks.filter((t) => t.done).length;
  return Math.round((done / lvl.tasks.length) * 100);
};

// first task checked in a level (green dot)
const levelStart = (lvl) => {
  const ds = lvl.tasks.filter((t) => t.done && t.doneAt).map((t) => t.doneAt);
  return ds.length ? Math.min(...ds) : null;
};
// last task checked in a level — only once the level is 100% (red dot)
const levelEnd = (lvl) => {
  if (pctOf(lvl) !== 100) return null;
  const ds = lvl.tasks.filter((t) => t.done && t.doneAt).map((t) => t.doneAt);
  return ds.length ? Math.max(...ds) : null;
};

function DateDots({ start, end }) {
  const dot = (c) => ({ width: 8, height: 8, borderRadius: '50%', background: c, flexShrink: 0 });
  const item = { display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-secondary,#c3c9d4)' };
  return (
    <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11 }}>
      <span style={item}><span style={dot('#22c55e')} />{start ? fmtShortDate(start) : '—'}</span>
      <span style={item}><span style={dot('#ef4444')} />{end ? fmtShortDate(end) : '—'}</span>
    </div>
  );
}

export default function ProjectStatusPanel({ project, massing, onUpdate }) {
  const [levels, setLevels] = useState(null);
  const [newTaskFor, setNewTaskFor] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [newTaskText, setNewTaskText] = useState('');
  const [saving, setSaving] = useState(false);
  const toggleExpand = (id) =>
      setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const floors = massing?.floors || 0;

  // load existing status from project, or seed from massing
  useEffect(() => {
    if (!project) return;
    const saved = project.projectStatus?.levels || [];
    const targetFloors = floors;

    if (targetFloors > 0) {
      // reconcile saved levels with current floor count
      const wanted = defaultLevels(targetFloors);
      const merged = wanted.map((w) => {
        const existing = saved.find((s) => s.id === w.id);
        // keep saved task progress, but always use current name
        return existing ? { ...w, name: w.name, tasks: existing.tasks } : w;
      });
      setLevels(merged);
    } else if (saved.length) {
      setLevels(saved);   // no live massing, show saved
    } else {
      setLevels(null);
    }
  }, [project?.id, floors]);

  // persist helper (debounced) — saves to backend like site data
  const persist = useCallback((nextLevels) => {
    if (!project) return;
    const updated = { ...project, projectStatus: { levels: nextLevels } };
    setSaving(true);
    fetch(`${API_BASE}/api/projects/${project.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updated),
    })
      .then((r) => r.json())
      .then(() => { onUpdate && onUpdate(updated); })
      .catch(() => {})
      .finally(() => setSaving(false));
  }, [project, onUpdate]);

  const commit = (nextLevels) => {
    setLevels(nextLevels);

    window.dispatchEvent(
      new CustomEvent('floorProgressChanged', {
        detail: {
          levels: nextLevels,
          projectId: project?.id,
        },
      })
    );

    persist(nextLevels);
  };

  const toggleTask = (li, ti) => {
    const next = levels.map((lvl, i) =>
      i !== li ? lvl : {
        ...lvl,
        tasks: lvl.tasks.map((t, j) => {
          if (j !== ti) return t;
          const nowDone = !t.done;
          return { ...t, done: nowDone, doneAt: nowDone ? Date.now() : null }; // stamp on check, clear on uncheck
        }),
      });
    commit(next);
  };

  const removeTask = (li, ti) => {
    const next = levels.map((lvl, i) =>
      i !== li ? lvl : { ...lvl, tasks: lvl.tasks.filter((_, j) => j !== ti) });
    commit(next);
  };

  const addTask = (li) => {
    if (!newTaskText.trim()) return;
    const next = levels.map((lvl, i) =>
      i !== li ? lvl : { ...lvl, tasks: [...lvl.tasks, { label: newTaskText.trim(), done: false }] });
    commit(next);
    setNewTaskText('');
    setNewTaskFor(null);
  };

  const overall = useMemo(() => {
    if (!levels?.length) return 0;
    const all = levels.flatMap((l) => l.tasks);
    if (!all.length) return 0;
    return Math.round((all.filter((t) => t.done).length / all.length) * 100);
  }, [levels]);

  // Foundation % → drive the 3D foundation slab (must stay ABOVE the early returns)
  const foundationPct = useMemo(() => {
    const lvl = levels?.find((l) => l.id === 'foundation');
    if (!lvl?.tasks?.length) return 0;
    return Math.round((lvl.tasks.filter((t) => t.done).length / lvl.tasks.length) * 100);
  }, [levels]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('foundationProgressChanged', { detail: { progress: foundationPct } }));
  }, [foundationPct]);

  // project start = first foundation task checked (green dot under Overall)
  const overallStart = useMemo(() => {
    const f = levels?.find((l) => l.id === 'foundation');
    return f ? levelStart(f) : null;
  }, [levels]);

  // project end = latest task date, only once the whole project is 100% (red dot under Overall)
  const overallEnd = useMemo(() => {
    if (overall !== 100 || !levels?.length) return null;
    const ds = levels.flatMap((l) => l.tasks).filter((t) => t.done && t.doneAt).map((t) => t.doneAt);
    return ds.length ? Math.max(...ds) : null;
  }, [levels, overall]);

  if (!project) {
    return <div style={{ padding: 16, fontSize: 13, color: 'var(--text-secondary,#c3c9d4)' }}>Select a project.</div>;
  }
  if (!levels) {
    return (
      <div style={{ padding: 16, fontSize: 13, color: 'var(--text-secondary,#c3c9d4)' }}>
        Build the massing first (left panel → Build Up) to generate the foundation and floor levels for tracking.
      </div>
    );
  }

  const card = { background: 'var(--surface-2,#1a2234)', border: '1px solid var(--border)',
    borderRadius: 10, padding: 12, marginBottom: 10 };
  const txtSec = 'var(--text-secondary,#c3c9d4)';
  const txtMut = 'var(--text-muted,#8b92a0)';

  return (
    <div style={{ fontSize: 13 }}>
      {/* overall */}
      <div style={{ ...card, background: 'var(--surface-1,#141a28)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <b>Overall Progress</b>
          <b style={{ color: colorFor(overall) }}>{overall}%</b>
        </div>
        <div style={{ height: 10, background: 'var(--surface-1)', borderRadius: 5, overflow: 'hidden' }}>
          <div style={{ width: `${overall}%`, height: '100%', background: colorFor(overall), transition: 'width .3s' }} />
        </div>
        <DateDots start={overallStart} end={overallEnd} />
        {saving && <div style={{ fontSize: 11, color: txtMut, marginTop: 4 }}>saving…</div>}
      </div>

      {/* per-level */}
      {levels.map((lvl, li) => {
        const pct = pctOf(lvl);
        return (
          <div key={lvl.id} style={card}>
            <div onClick={() => toggleExpand(lvl.id)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: 6, cursor: 'pointer' }}>
              <b>
                <span style={{ display: 'inline-block', width: 12, color: 'var(--text-muted,#8b92a0)' }}>
                  {expanded[lvl.id] ? '▾' : '▸'}
                </span>{' '}
                {lvl.name}
              </b>
              <span style={{ color: colorFor(pct), fontWeight: 600 }}>{pct}%</span>
            </div>
            <div style={{ height: 8, background: 'var(--surface-1)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: colorFor(pct), transition: 'width .3s' }} />
            </div>

            {/* per-floor start / end dots */}
            <DateDots start={levelStart(lvl)} end={levelEnd(lvl)} />

            {expanded[lvl.id] && (
              <div style={{ marginTop: 10 }}>
                {lvl.tasks.map((t, ti) => (
                  <div key={ti} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                    <input type="checkbox" checked={t.done} onChange={() => toggleTask(li, ti)}
                      style={{ cursor: 'pointer', flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0, color: t.done ? txtMut : txtSec,
                      textDecoration: t.done ? 'line-through' : 'none' }}>{t.label}</span>
                    {t.done && t.doneAt && (
                      <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 500,
                        color: 'var(--text-accent,#7fb4f0)', textDecoration: 'none' }}>
                        ({fmtShortDate(t.doneAt)})
                      </span>
                    )}
                    <button onClick={() => removeTask(li, ti)} title="Remove task"
                      style={{ background: 'none', border: 'none', color: txtMut, cursor: 'pointer',
                        fontSize: 14, flexShrink: 0 }}>×</button>
                  </div>
                ))}

                {newTaskFor === lvl.id ? (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <input autoFocus value={newTaskText} onChange={(e) => setNewTaskText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addTask(li)}
                      placeholder="New task…"
                      style={{ flex: 1, fontSize: 12, padding: '4px 6px', borderRadius: 6,
                        border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)' }} />
                    <button onClick={() => addTask(li)} style={{ fontSize: 12, cursor: 'pointer' }}>Add</button>
                  </div>
                ) : (
                  <button onClick={() => { setNewTaskFor(lvl.id); setNewTaskText(''); }}
                    style={{ marginTop: 6, background: 'none', border: 'none', color: 'var(--text-accent,#7fb4f0)',
                      cursor: 'pointer', fontSize: 12, padding: 0 }}>+ Add task</button>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div style={{ fontSize: 11, color: txtMut, marginTop: 4 }}>
        Red 0% · Yellow in-progress · Green 100%. Progress saves automatically.
      </div>
    </div>
  );
}
